import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Shield, Users, Loader2, LogIn, LogOut, ExternalLink, RefreshCw,
    Search, Filter, Mail
} from 'lucide-react';
import ClientDrawer from './ClientDrawer';
import AdminNav from '../../components/admin/AdminNav';
import { useAuth } from '../../hooks/useAuth';

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGE_ORDER = [
    'Committed',
    'Onboarding',
    'Building',
    'Testing',
    'Shipped',
    'Delivered',
    'Training Booked',
    'Active',
    'Close Lost',
];

const STAGE_COLORS = {
    'Committed':       'bg-[#7B61FF]/20 text-[#7B61FF] ring-[#7B61FF]/30',
    'Onboarding':      'bg-blue-500/20 text-blue-400 ring-blue-500/30',
    'Building':        'bg-orange-500/20 text-orange-400 ring-orange-500/30',
    'Testing':         'bg-amber-500/20 text-amber-400 ring-amber-500/30',
    'Shipped':         'bg-cyan-500/20 text-cyan-400 ring-cyan-500/30',
    'Delivered':       'bg-green-500/20 text-green-400 ring-green-500/30',
    'Training Booked': 'bg-teal-500/20 text-teal-400 ring-teal-500/30',
    'Active':          'bg-emerald-500/20 text-emerald-400 ring-emerald-500/30',
    'Close Lost':      'bg-red-500/20 text-red-400 ring-red-500/30',
};

const getTimeAgo = (timestamp) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
};

const isTestClient = (client) => {
    const email = (client.email || '').toLowerCase();
    const name  = (client.name  || '').toLowerCase();
    return email.includes('test@') || email.includes('example.com') ||
           name.includes('test client') || name.includes('vito test');
};

const daysAgo = (dateStr) => {
    if (!dateStr) return 0;
    return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
};

const allPrereqsDone = (progress = {}) =>
    progress.intake_complete &&
    progress.apple_id_ready &&
    progress.tailscale_ready &&
    progress.agent_email_ready &&
    progress.credentials_submitted;

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ label, value, accent }) => (
    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl px-5 py-4 flex flex-col gap-1">
        <span className={`text-2xl font-bold font-data ${accent || 'text-[#F0EFF4]'}`}>{value}</span>
        <span className="text-xs text-[#F0EFF4]/40 uppercase tracking-wider font-data">{label}</span>
    </div>
);

const UrgencyDot = ({ client }) => {
    const days = daysAgo(client.created_at);
    const p    = client.progress || {};

    let color = 'bg-emerald-400';
    let title = 'On track';

    if (!p.intake_complete && days >= 7) {
        color = 'bg-red-400';
        title = `Intake not done — ${days}d since joining`;
    } else if (p.intake_complete && !allPrereqsDone(p)) {
        const intakeDays = daysAgo(p.intake_submitted_at);
        if (intakeDays >= 5) {
            color = 'bg-yellow-400';
            title = `Pre-reqs stalled — ${intakeDays}d since intake`;
        }
    }

    return (
        <span
            className={`inline-block w-2 h-2 rounded-full shrink-0 ${color}`}
            title={title}
        />
    );
};

const DaysActiveBadge = ({ days }) => {
    const color = days >= 15 ? 'text-red-400' : days >= 8 ? 'text-yellow-400' : 'text-[#F0EFF4]/60';
    return <span className={`font-data text-sm font-semibold ${color}`}>{days}d</span>;
};

const BooleanPill = ({ value, label, onClick, clickable }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={!clickable}
        title={clickable ? `Click to mark ${label} as ${value ? 'incomplete' : 'complete'}` : undefined}
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-data font-medium transition-all
        ${value
            ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/30'
            : 'bg-white/5 text-white/30 ring-1 ring-white/10'}
        ${clickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
        ${clickable && value  ? 'hover:bg-emerald-500/30 hover:shadow-[0_0_10px_rgba(16,185,129,0.2)]' : ''}
        ${clickable && !value ? 'hover:bg-white/10' : ''}`}
    >
        {label}
    </button>
);

const StageBadge = ({ stage, onStageSelect, clientId }) => {
    const [open, setOpen] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const ref = React.useRef(null);

    // Close dropdown on outside click
    React.useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const colorClass = STAGE_COLORS[stage] || 'bg-white/5 text-white/40 ring-white/10';

    const handleSelect = async (newStage) => {
        if (newStage === stage) { setOpen(false); return; }
        setSaving(true);
        try {
            await onStageSelect(clientId, newStage);
        } finally {
            setSaving(false);
            setOpen(false);
        }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
                title="Click to change stage"
                disabled={saving}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-data font-semibold ring-1 transition-all cursor-pointer hover:scale-105 ${saving ? 'opacity-50' : ''} ${colorClass}`}
            >
                {saving ? '...' : (stage || '—')}
                <svg className="w-3 h-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {open && (
                <div className="absolute z-50 mt-1 left-0 min-w-[180px] bg-[#18181B] border border-[#F0EFF4]/10 rounded-lg shadow-xl py-1 animate-in fade-in slide-in-from-top-1">
                    {STAGE_ORDER.map(s => {
                        const sc = STAGE_COLORS[s] || '';
                        const isCurrent = s === stage;
                        return (
                            <button
                                key={s}
                                onClick={(e) => { e.stopPropagation(); handleSelect(s); }}
                                className={`w-full text-left px-3 py-1.5 text-xs font-data transition-colors flex items-center gap-2
                                    ${isCurrent ? 'bg-[#F0EFF4]/10 font-bold' : 'hover:bg-[#F0EFF4]/5'}`}
                            >
                                <span className={`w-2 h-2 rounded-full ${sc.split(' ')[0] || 'bg-white/20'}`} />
                                {s}
                                {isCurrent && <span className="text-[#7B61FF] ml-auto">✓</span>}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminDashboard = () => {
    const auth = useAuth();
    const { authenticated, loading: authLoading, error: authError, password, user, method, logout, getAuthHeaders } = auth;

    const [email,           setEmail          ] = useState('');
    const [loginPassword,   setLoginPassword  ] = useState('');
    const [loginLoading,    setLoginLoading   ] = useState(false);
    const [loginError,      setLoginError     ] = useState('');
    const [pipeline,        setPipeline       ] = useState([]);
    const [resetSent,       setResetSent      ] = useState(false);

    const [drawerClient,    setDrawerClient   ] = useState(null);
    const [activityFeed,    setActivityFeed   ] = useState([]);

    // Filter state
    const [showTestClients, setShowTestClients] = useState(false);
    const [stageFilter,     setStageFilter    ] = useState('All');
    const [searchQuery,     setSearchQuery    ] = useState('');

    // ── Auto-load pipeline when session exists ──────────────────────────────

    React.useEffect(() => {
        if (authenticated && pipeline.length === 0 && password) {
            (async () => {
                try {
                    const res = await fetch(`/api/get-admin-pipeline?password=${encodeURIComponent(password)}`, { headers: getAuthHeaders() });
                    const data = await res.json();
                    if (res.ok) setPipeline(data.pipeline || []);
                } catch (e) {
                    console.error('Auto-load pipeline failed:', e);
                }
            })();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authenticated, pipeline.length, password]);

    // ── Load activity feed ──────────────────────────────────────────────────
    React.useEffect(() => {
        if (authenticated) {
            (async () => {
                try {
                    const res = await fetch('/api/get-activity-feed?limit=15', { headers: getAuthHeaders() });
                    const data = await res.json();
                    if (res.ok) setActivityFeed(data.feed || []);
                } catch (e) {
                    console.error('Activity feed load failed:', e);
                }
            })();
        }
    }, [authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Auth ─────────────────────────────────────────────────────────────────

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setLoginLoading(true);
        setLoginError('');
        try {
            const result = await auth.login(email, loginPassword);
            if (result.success) {
                // Fetch pipeline data after successful auth
                const pw = loginPassword;
                const headers = result.method === 'jwt' ? getAuthHeaders() : {};
                const res = await fetch(`/api/get-admin-pipeline?password=${encodeURIComponent(pw)}`, { headers });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Failed to load pipeline');
                setPipeline(data.pipeline || []);
            } else {
                setLoginError('Invalid email or password');
            }
        } catch (err) {
            setLoginError(err.message);
        } finally {
            setLoginLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) return setLoginError('Enter your email first');
        const { success, error } = await auth.resetPassword(email);
        if (success) {
            setResetSent(true);
            setLoginError('');
        } else {
            setLoginError(error || 'Failed to send reset email');
        }
    };

    const handleLogout = async () => {
        await logout();
        setPipeline([]);
    };

    // ── Toggle boolean prereq field ──────────────────────────────────────────

    const handleToggleStatus = async (clientId, field, currentValue) => {
        if (!window.confirm(`Mark ${field} as ${!currentValue ? 'COMPLETE' : 'INCOMPLETE'}?`)) return;
        try {
            const res = await fetch('/api/update-client-status', {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ client_id: clientId, [field]: !currentValue }),
            });
            if (!res.ok) throw new Error('Failed to update');
            setPipeline(prev => prev.map(c =>
                c.id === clientId
                    ? { ...c, progress: { ...c.progress, [field]: !currentValue } }
                    : c
            ));
        } catch (err) { alert(err.message); }
    };

    // ── Advance stage ────────────────────────────────────────────────────────

    const handleStageChange = async (clientId, targetStage) => {
        try {
            const res = await fetch('/api/update-client-status', {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ client_id: clientId, stage: targetStage }),
            });
            if (!res.ok) throw new Error('Failed to update stage');
            setPipeline(prev => prev.map(c =>
                c.id === clientId ? { ...c, stage: targetStage } : c
            ));
        } catch (err) { alert(err.message); }
    };

    // ── Post activity update ─────────────────────────────────────────────────

    const handlePostUpdate = async (e, clientId) => {
        e.preventDefault();
        const fd        = new FormData(e.target);
        const submitBtn = e.target.querySelector('button[type="submit"]');
        const ogText    = submitBtn.textContent;
        submitBtn.disabled    = true;
        submitBtn.textContent = 'Posting…';
        try {
            const res = await fetch('/api/post-client-update', {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    client_id:  clientId,
                    message:    fd.get('message'),
                    link_url:   fd.get('link_url'),
                    link_label: fd.get('link_label'),
                    type:       fd.get('type') || 'info',
                }),
            });
            if (!res.ok) throw new Error('Failed to post update');
            const { update } = await res.json();
            setPipeline(prev => prev.map(c =>
                c.id === clientId
                    ? { ...c, client_updates: [update, ...(c.client_updates || [])] }
                    : c
            ));
            e.target.reset();
        } catch (err) {
            alert(err.message);
        } finally {
            submitBtn.disabled    = false;
            submitBtn.textContent = ogText;
        }
    };

    // ── Filtered pipeline ────────────────────────────────────────────────────

    const filteredPipeline = useMemo(() => {
        return pipeline.filter(client => {
            if (!showTestClients && isTestClient(client)) return false;
            if (stageFilter !== 'All' && client.stage !== stageFilter) return false;
            if (searchQuery) {
                const q = searchQuery.toLowerCase();
                const nameMatch    = (client.name    || '').toLowerCase().includes(q);
                const companyMatch = (client.company || '').toLowerCase().includes(q);
                if (!nameMatch && !companyMatch) return false;
            }
            return true;
        });
    }, [pipeline, showTestClients, stageFilter, searchQuery]);

    // ── Stats ────────────────────────────────────────────────────────────────

    const stats = useMemo(() => {
        const real = pipeline.filter(c => !isTestClient(c));
        return {
            total:       real.length,
            awaitIntake: real.filter(c => !c.progress?.intake_complete).length,
            inProgress:  real.filter(c =>  c.progress?.intake_complete && !allPrereqsDone(c.progress)).length,
            readyBuild:  real.filter(c =>  allPrereqsDone(c.progress)).length,
        };
    }, [pipeline]);

    // ─── Login screen ─────────────────────────────────────────────────────────

    if (authLoading) {
        return (
            <div className="min-h-screen bg-[#0A0A14] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#7B61FF] animate-spin" />
            </div>
        );
    }

    if (!authenticated) {
        return (
            <div className="min-h-screen bg-[#0A0A14] flex items-center justify-center p-6">
                <div className="w-full max-w-sm">
                    <div className="text-center mb-8">
                        <Shield className="w-10 h-10 text-[#7B61FF] mx-auto mb-4" />
                        <h1 className="text-2xl font-bold text-[#F0EFF4]">
                            Roof<span className="text-[#7B61FF]">Claw</span> Admin
                        </h1>
                        <p className="text-[#F0EFF4]/40 text-sm mt-1">Pipeline Dashboard</p>
                    </div>
                    {resetSent ? (
                        <div className="text-center space-y-4">
                            <Mail className="w-10 h-10 text-emerald-400 mx-auto" />
                            <p className="text-[#F0EFF4] text-sm">Password reset link sent to <strong>{email}</strong></p>
                            <button
                                onClick={() => setResetSent(false)}
                                className="text-sm text-[#7B61FF] hover:text-[#6B51EF] transition-colors"
                            >
                                ← Back to login
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleLogin} className="space-y-4">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email"
                                className="w-full px-4 py-3 bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl text-[#F0EFF4]
                                           placeholder:text-[#F0EFF4]/30 focus:outline-none focus:border-[#7B61FF]/50 transition-colors"
                                autoFocus
                            />
                            <input
                                type="password"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                placeholder="Password"
                                className="w-full px-4 py-3 bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl text-[#F0EFF4]
                                           placeholder:text-[#F0EFF4]/30 focus:outline-none focus:border-[#7B61FF]/50 transition-colors"
                            />
                            {loginError && <p className="text-red-400 text-sm text-center">{loginError}</p>}
                            <button
                                type="submit"
                                disabled={loginLoading || !loginPassword}
                                className="w-full py-3 bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-semibold rounded-xl
                                           transition-all flex items-center justify-center gap-2 disabled:opacity-40"
                            >
                                {loginLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                                Access Dashboard
                            </button>
                            <button
                                type="button"
                                onClick={handleForgotPassword}
                                className="w-full text-sm text-[#F0EFF4]/30 hover:text-[#7B61FF] transition-colors"
                            >
                                Forgot password?
                            </button>
                        </form>
                    )}
                </div>
            </div>
        );
    }

    // ─── Dashboard ────────────────────────────────────────────────────────────

    return (
        <div className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] font-heading">

            {/* Header */}
            <header className="border-b border-[#F0EFF4]/10 bg-[#0A0A14] sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-[#7B61FF]" />
                        <span className="font-bold text-lg">
                            Roof<span className="text-[#7B61FF]">Claw</span>
                            <span className="text-[#F0EFF4]/40 text-sm font-data ml-2">ADMIN</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-[#F0EFF4]/50">
                        {user && (
                            <span className="text-xs text-[#F0EFF4]/30 hidden sm:inline">
                                {user.email}
                            </span>
                        )}
                        {method && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-data uppercase tracking-wider ${
                                method === 'jwt' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-yellow-500/10 text-yellow-400'
                            }`}>
                                {method === 'jwt' ? '🔐 Secure' : '🔑 Legacy'}
                            </span>
                        )}
                        <span className="flex items-center gap-1.5">
                            <Users className="w-4 h-4" />
                            {pipeline.length} client{pipeline.length !== 1 ? 's' : ''}
                        </span>
                        <button
                            onClick={async (e) => {
                                e.preventDefault();
                                setLoginLoading(true);
                                try {
                                    const res = await fetch(`/api/get-admin-pipeline?password=${encodeURIComponent(password)}`, { headers: getAuthHeaders() });
                                    const data = await res.json();
                                    if (res.ok) setPipeline(data.pipeline || []);
                                } finally {
                                    setLoginLoading(false);
                                }
                            }}
                            disabled={loginLoading}
                            className="p-1.5 bg-[#F0EFF4]/5 hover:bg-[#F0EFF4]/10 border border-[#F0EFF4]/10 rounded-lg transition-colors text-[#F0EFF4]/80 hover:text-[#F0EFF4] flex items-center gap-2"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loginLoading ? 'animate-spin' : ''}`} />
                            Sync
                        </button>
                        <button
                            onClick={handleLogout}
                            className="p-1.5 bg-[#F0EFF4]/5 hover:bg-red-500/20 border border-[#F0EFF4]/10 rounded-lg transition-colors text-[#F0EFF4]/40 hover:text-red-400 flex items-center gap-2"
                            title="Log out"
                        >
                            <LogOut className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">

                <AdminNav />

                {/* Stats bar */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard label="Total Clients"        value={stats.total}       accent="text-[#F0EFF4]" />
                    <StatCard label="Awaiting Intake"      value={stats.awaitIntake} accent="text-yellow-400" />
                    <StatCard label="Pre-Reqs In Progress" value={stats.inProgress}  accent="text-blue-400" />
                    <StatCard label="Ready For Build"      value={stats.readyBuild}  accent="text-emerald-400" />
                </div>

                {/* Activity Feed */}
                {activityFeed.length > 0 && (
                    <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-4">
                        <h3 className="text-xs font-bold text-[#F0EFF4]/40 uppercase tracking-wider mb-3">Recent Activity</h3>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {activityFeed.slice(0, 10).map(event => {
                                const timeAgo = getTimeAgo(event.timestamp);
                                if (event.type === 'stage_change') {
                                    return (
                                        <div key={event.id} className="flex items-center gap-3 text-sm">
                                            <span className="w-1.5 h-1.5 rounded-full bg-[#7B61FF] shrink-0" />
                                            <span className="text-[#F0EFF4]/80 truncate">
                                                <strong className="text-[#F0EFF4]">{event.client_name}</strong>
                                                {' '}moved from{' '}
                                                <span className="text-[#F0EFF4]/50">{event.from_stage || '—'}</span>
                                                {' → '}
                                                <span className="text-[#7B61FF]">{event.to_stage}</span>
                                            </span>
                                            <span className="text-[#F0EFF4]/30 text-xs ml-auto shrink-0">{timeAgo}</span>
                                        </div>
                                    );
                                }
                                if (event.type === 'intake_submitted') {
                                    return (
                                        <div key={event.id} className="flex items-center gap-3 text-sm">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                                            <span className="text-[#F0EFF4]/80 truncate">
                                                <strong className="text-[#F0EFF4]">{event.client_name}</strong> submitted intake form
                                            </span>
                                            <span className="text-[#F0EFF4]/30 text-xs ml-auto shrink-0">{timeAgo}</span>
                                        </div>
                                    );
                                }
                                if (event.type === 'credential_submitted') {
                                    return (
                                        <div key={event.id} className="flex items-center gap-3 text-sm">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                                            <span className="text-[#F0EFF4]/80 truncate">
                                                <strong className="text-[#F0EFF4]">{event.client_name}</strong> submitted credentials
                                            </span>
                                            <span className="text-[#F0EFF4]/30 text-xs ml-auto shrink-0">{timeAgo}</span>
                                        </div>
                                    );
                                }
                                return (
                                    <div key={event.id} className="flex items-center gap-3 text-sm">
                                        <span className="w-1.5 h-1.5 rounded-full bg-[#F0EFF4]/20 shrink-0" />
                                        <span className="text-[#F0EFF4]/60 truncate">
                                            <strong className="text-[#F0EFF4]">{event.client_name}</strong> — {event.type.replace(/_/g, ' ')}
                                        </span>
                                        <span className="text-[#F0EFF4]/30 text-xs ml-auto shrink-0">{timeAgo}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Filter bar */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#F0EFF4]/30" />
                        <input
                            type="text"
                            placeholder="Search name or company…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-8 pr-4 py-2 bg-[#18181B] border border-[#F0EFF4]/10 rounded-lg text-sm text-[#F0EFF4] placeholder:text-[#F0EFF4]/30 focus:outline-none focus:border-[#7B61FF]/50 transition-colors"
                        />
                    </div>

                    {/* Stage filter */}
                    <div className="relative">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#F0EFF4]/30 pointer-events-none" />
                        <select
                            value={stageFilter}
                            onChange={(e) => setStageFilter(e.target.value)}
                            className="pl-8 pr-4 py-2 bg-[#18181B] border border-[#F0EFF4]/10 rounded-lg text-sm text-[#F0EFF4] focus:outline-none focus:border-[#7B61FF]/50 appearance-none transition-colors"
                        >
                            <option value="All">All Stages</option>
                            {STAGE_ORDER.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    {/* Test client toggle */}
                    <button
                        type="button"
                        onClick={() => setShowTestClients(v => !v)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-data font-medium border transition-all
                        ${showTestClients
                            ? 'bg-[#7B61FF]/20 border-[#7B61FF]/40 text-[#7B61FF]'
                            : 'bg-[#18181B] border-[#F0EFF4]/10 text-[#F0EFF4]/50 hover:text-[#F0EFF4]'}`}
                    >
                        {showTestClients ? '👁 Showing Test Clients' : '🙈 Hide Test Clients'}
                    </button>
                </div>

                {/* Table */}
                <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-[#F0EFF4]/10">
                                    <th className="text-left px-6 py-4 text-xs text-[#F0EFF4]/50 font-data font-medium uppercase tracking-wider">Client</th>
                                    <th className="text-left px-4 py-4 text-xs text-[#F0EFF4]/50 font-data font-medium uppercase tracking-wider">Agent Name</th>
                                    <th className="text-left px-4 py-4 text-xs text-[#F0EFF4]/50 font-data font-medium uppercase tracking-wider">Stage</th>
                                    <th className="text-left px-4 py-4 text-xs text-[#F0EFF4]/50 font-data font-medium uppercase tracking-wider">Pre-Reqs</th>
                                    <th className="text-left px-4 py-4 text-xs text-[#F0EFF4]/50 font-data font-medium uppercase tracking-wider">Days Active</th>
                                    <th className="text-right px-6 py-4 text-xs text-[#F0EFF4]/50 font-data font-medium uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPipeline.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-[#F0EFF4]/30 text-sm">
                                            No clients match the current filters.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPipeline.map((client) => {
                                        const days    = daysAgo(client.created_at);
                                        const p       = client.progress || {};
                                        const agentName = client.intake?.agent_name || '—';

                                        return (
                                            <React.Fragment key={client.id}>
                                                <tr
                                                    className="border-b border-[#F0EFF4]/5 transition-colors group cursor-pointer hover:bg-[#F0EFF4]/[0.02]"
                                                    onClick={() => setDrawerClient(client)}
                                                >
                                                    {/* Client name + email + urgency dot */}
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <UrgencyDot client={client} />
                                                            <div>
                                                                <Link
                                                                    to={`/admin/client/${client.id}`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="font-semibold text-sm hover:text-[#7B61FF] transition-colors"
                                                                >
                                                                    {client.name || '—'}
                                                                </Link>
                                                                <div className="text-xs text-[#F0EFF4]/40 mt-0.5">{client.email || '—'}</div>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Agent Name */}
                                                    <td className="px-4 py-4 text-sm text-[#F0EFF4]/70 font-data">
                                                        {agentName}
                                                    </td>

                                                    {/* Stage — clickable, cycles forward */}
                                                    <td className="px-4 py-4">
                                                        <StageBadge
                                                            stage={client.stage || 'Committed'}
                                                            clientId={client.id}
                                                            onStageSelect={handleStageChange}
                                                        />
                                                    </td>

                                                    {/* Pre-Reqs — all 5 pills clickable */}
                                                    <td className="px-4 py-4">
                                                        <div className="flex flex-wrap gap-1.5">
                                                            <BooleanPill
                                                                value={p.intake_complete}
                                                                label="Intake"
                                                                clickable={true}
                                                                onClick={(e) => { e.stopPropagation(); handleToggleStatus(client.id, 'intake_complete', p.intake_complete); }}
                                                            />
                                                            <BooleanPill
                                                                value={p.apple_id_ready}
                                                                label="Apple ID"
                                                                clickable={true}
                                                                onClick={(e) => { e.stopPropagation(); handleToggleStatus(client.id, 'apple_id_ready', p.apple_id_ready); }}
                                                            />
                                                            <BooleanPill
                                                                value={p.tailscale_ready}
                                                                label="Tailscale"
                                                                clickable={true}
                                                                onClick={(e) => { e.stopPropagation(); handleToggleStatus(client.id, 'tailscale_ready', p.tailscale_ready); }}
                                                            />
                                                            <BooleanPill
                                                                value={p.agent_email_ready}
                                                                label="Email"
                                                                clickable={true}
                                                                onClick={(e) => { e.stopPropagation(); handleToggleStatus(client.id, 'agent_email_ready', p.agent_email_ready); }}
                                                            />
                                                            <BooleanPill
                                                                value={p.credentials_submitted}
                                                                label="Creds"
                                                                clickable={true}
                                                                onClick={(e) => { e.stopPropagation(); handleToggleStatus(client.id, 'credentials_submitted', p.credentials_submitted); }}
                                                            />
                                                        </div>
                                                    </td>

                                                    {/* Days Active */}
                                                    <td className="px-4 py-4">
                                                        <DaysActiveBadge days={days} />
                                                    </td>

                                                    {/* Actions */}
                                                    <td className="px-6 py-4 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <a
                                                                href={client.portal_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F0EFF4]/5 hover:bg-[#7B61FF]/20 border border-[#F0EFF4]/10 rounded-lg text-xs text-[#F0EFF4] hover:text-[#7B61FF] transition-all font-semibold"
                                                            >
                                                                <ExternalLink className="w-3.5 h-3.5" />
                                                                Open Portal
                                                            </a>

                                                        </div>
                                                    </td>
                                                </tr>


                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* Slide-out Drawer */}
            {drawerClient && (
                <ClientDrawer
                    client={drawerClient}
                    password={password}
                    onClose={() => setDrawerClient(null)}
                    onStageChange={async (clientId, targetStage) => {
                        await handleStageChange(clientId, targetStage);
                        setDrawerClient(prev => prev && prev.id === clientId ? { ...prev, stage: targetStage } : prev);
                    }}
                    onToggleStatus={async (clientId, field, currentValue) => {
                        await handleToggleStatus(clientId, field, currentValue);
                        setDrawerClient(prev => prev && prev.id === clientId
                            ? { ...prev, progress: { ...prev.progress, [field]: !currentValue } }
                            : prev
                        );
                    }}
                    onPostUpdate={(e, clientId) => {
                        handlePostUpdate(e, clientId);
                    }}
                />
            )}
        </div>
    );
};

export default AdminDashboard;
